/* cali — "Add a strap" CTA on bag-only product pages.
 *
 * Loads the Bag Builder on demand and opens it with the bag colour the customer is
 * looking at already selected, positioned on the strap question. Closing the builder
 * leaves the customer on this page — nothing here navigates.
 *
 * Contract with the patched customization-app.js:
 *   window.caliBuilderInit = {
 *     bagVariantId: <BUILDER component variant id>,   // not the id of this page's variant
 *     startStep:    <0-based index into priority-sorted questions>,
 *     startingPoint:<string stamped onto the cart line for attribution>
 *   }
 *   window.caliBuilderOpen(init)  — registered by the patch, used to re-open.
 *
 * The builder's own answers reference BUILDER component variant ids, which differ from
 * this product's variant ids even though the SKUs are identical
 * ("Ultraviolet" / 55399475708280 here vs "Gridstop Ultraviolet" / 55218754126200 there).
 * SKU is therefore the join key, resolved against the fetched config.
 *
 * Nothing is fetched until the button is tapped.
 */
(function () {
  'use strict';

  var LOADED = false;
  var LOADING = null;
  var CONFIG = null;

  function root() {
    return document.querySelector('[data-cali-strap-cta]');
  }

  function track(event, payload) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: event }, payload || {}));
    } catch (e) {
      /* analytics must never break the button */
    }
  }

  function currentVariantId() {
    // The add-to-cart form's id input is the source of truth for the selected variant
    // in Motion, however the customer changed colour (swatch, dropdown, ?variant= on load).
    var input = document.querySelector('form[action*="/cart/add"] [name="id"]');
    if (input && input.value) return String(input.value).trim();
    var fromUrl = new URLSearchParams(window.location.search).get('variant');
    return fromUrl ? String(fromUrl).trim() : '';
  }

  function variantSkuMap(el) {
    try {
      return JSON.parse(el.getAttribute('data-variant-skus') || '{}');
    } catch (e) {
      return {};
    }
  }

  // SKU -> builder component variant id, from the builder's own configuration.
  function builderVariantForSku(cfg, sku) {
    if (!sku || !cfg || !cfg.customizeProducts) return '';
    for (var i = 0; i < cfg.customizeProducts.length; i++) {
      var variants = cfg.customizeProducts[i].variants || [];
      for (var j = 0; j < variants.length; j++) {
        if (variants[j] && String(variants[j].sku) === String(sku)) {
          return String(variants[j].id);
        }
      }
    }
    return '';
  }

  function setStatus(el, message, state) {
    var node = el.querySelector('[data-cali-strap-cta-status]');
    if (!node) return;
    node.textContent = message || '';
    if (state) node.setAttribute('data-state', state);
    else node.removeAttribute('data-state');
  }

  function busy(el, isBusy) {
    var btn = el.querySelector('[data-cali-strap-cta-btn]');
    if (!btn) return;
    if (isBusy) btn.setAttribute('aria-busy', 'true');
    else btn.removeAttribute('aria-busy');
  }

  function fetchConfig(el) {
    if (CONFIG) return Promise.resolve(CONFIG);
    var url = el.getAttribute('data-config-url');
    // No Accept: application/json here — Shopify answers that header with its own
    // product JSON payload instead of rendering the ?view= template.
    return fetch(url, { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('config HTTP ' + r.status);
        return r.text();
      })
      .then(function (text) {
        var cfg;
        try {
          cfg = JSON.parse(text);
        } catch (e) {
          throw new Error('config was not JSON (is the builderconfig template present?)');
        }
        if (!cfg || cfg.ok !== true) throw new Error('config payload missing');
        if (!cfg.customizeProducts || !cfg.customizeProducts.length) {
          throw new Error('builder has no components configured');
        }
        CONFIG = cfg;
        return cfg;
      });
  }

  // Mirror of the globals product-template.liquid sets on a real builder page.
  // isBundleMapping is deliberately NOT set: it switches the builder into prebuilt
  // mode ("Customise it" copy plus a second add-to-cart button).
  function applyGlobals(cfg) {
    window.customizeProducts = cfg.customizeProducts;
    window.product_virtual_variant_id = cfg.virtualVariantId || '';
    window.presaleData = cfg.presaleData || {};
    window.currency = cfg.currency;
    window.completionTitle = cfg.completionTitle;
    window.soldoutTitle = cfg.soldoutTitle;
    window.completionBenefit1 = cfg.completionBenefit1;
    window.completionBenefit2 = cfg.completionBenefit2;
    window.completionBenefit3 = cfg.completionBenefit3;
    window.completionImageIcon1 = cfg.completionImageIcon1;
    window.completionImageIcon2 = cfg.completionImageIcon2;
    window.completionImageIcon3 = cfg.completionImageIcon3;
    // These become _designImage / _designProductUrl / _designProductId on the cart line,
    // so they must point at the BUILDER product, never at this bag-only page.
    window.featured_image = cfg.featuredImage;
    window.featured_product_url = cfg.featuredProductUrl;
    window.bundle_design_id = cfg.bundleDesignId || '';
  }

  function ensureMount(cfg) {
    var mount = document.getElementById('react-customization-app');
    if (!mount) {
      mount = document.createElement('div');
      mount.id = 'react-customization-app';
      mount.setAttribute('data-cali-headless', 'true'); // hides the builder's own trigger
      document.body.appendChild(mount);
    }
    // The handle must be the BUILDER's: it is what the app fetches its bundle with.
    mount.setAttribute('data-product-id', cfg.productId || '');
    mount.setAttribute('data-product-handle', cfg.handle || '');
    return mount;
  }

  function injectBundle(el) {
    if (LOADED) return Promise.resolve();
    if (LOADING) return LOADING;
    LOADING = new Promise(function (resolve, reject) {
      var src = el.getAttribute('data-bundle-src');
      if (!src) return reject(new Error('no bundle src'));
      var s = document.createElement('script');
      s.src = src;
      s.async = true;
      s.onload = function () {
        LOADED = true;
        resolve();
      };
      s.onerror = function () {
        LOADING = null;
        reject(new Error('builder failed to load'));
      };
      document.head.appendChild(s);
    });
    return LOADING;
  }

  // The bundle's startStep only drives the MOBILE stepper, which shows one question
  // at a time. Desktop renders every visible question as one scrolling list, so it
  // opens at the top — on Base Bag, the question the customer already answered on the
  // PDP. Scroll the target question to the top of the panel so both layouts land on
  // the strap question. Index is into the VISIBLE questions, matching startStep.
  function focusStep(stepIndex) {
    if (!(stepIndex > 0)) return;

    // The bag preview loads after the panel paints, and the reflow pushes the list
    // back down — a single scroll lands on the strap question and then drifts back to
    // Base Bag. So re-assert the position until the layout settles, and stop the
    // moment the customer scrolls for themselves (wheel/touch/key only: assigning
    // scrollTop fires 'scroll' too, which would cancel us immediately).
    var cancelled = false;
    var deadline = 2600;
    var elapsed = 0;

    function release() {
      cancelled = true;
      ['wheel', 'touchstart', 'keydown'].forEach(function (evt) {
        document.removeEventListener(evt, release, true);
      });
    }
    ['wheel', 'touchstart', 'keydown'].forEach(function (evt) {
      document.addEventListener(evt, release, true);
    });

    function apply() {
      var scroller = document.querySelector('.bag-customizer-container .left-content');
      var titles = document.querySelectorAll('.bag-customizer-container .section-title');
      if (!scroller || !titles.length) return false;
      if (stepIndex >= titles.length) return true; // hidden by a rule; leave at top
      var section = titles[stepIndex].closest('.section-header') || titles[stepIndex];
      var delta = section.getBoundingClientRect().top - scroller.getBoundingClientRect().top;
      if (Math.abs(delta) > 2) scroller.scrollTop += delta;
      return true;
    }

    (function tick(wait) {
      if (cancelled || elapsed > deadline) return release();
      apply();
      elapsed += wait;
      setTimeout(function () { tick(wait); }, wait);
    })(150);
  }

  function buildInit(el, cfg) {
    var pdpVariantId = currentVariantId();
    var sku = variantSkuMap(el)[pdpVariantId] || '';
    var handle = (window.location.pathname.split('/products/')[1] || '').split('?')[0];
    // Theme setting is 1-based and human-facing (1 = bag, 2 = strap); the builder
    // wants a 0-based index into its priority-sorted question list.
    var step = parseInt(el.getAttribute('data-start-step'), 10);
    if (isNaN(step) || step < 1) step = 2;

    return {
      bagVariantId: builderVariantForSku(cfg, sku),
      bagSku: sku,
      pdpVariantId: pdpVariantId,
      startStep: step - 1,
      startingPoint: 'bagonly:' + handle,
      source: 'pdp-strap-cta'
    };
  }

  // The builder mounts at the END of <body> (ensureMount appends it on bag-only pages,
  // which have no builder of their own). Opening and closing it moves focus into that
  // node, so the browser scrolls the PDP to the very bottom once the dialog goes away.
  // Remember where the customer was and put them back on the button they pressed.
  function guardScroll() {
    var y = window.scrollY || window.pageYOffset || 0;
    var sawOpen = false;
    var ticks = 0;

    var poll = setInterval(function () {
      // .bag-customizer-container is mounted only while the builder is open, so its
      // presence is the open/closed signal. Do NOT test .customizer-dialog-container:
      // that survives the close as a 60px "Start Building" stub and never reads shut.
      var isOpen = !!document.querySelector('.bag-customizer-container');

      if (isOpen) {
        sawOpen = true;
      } else if (sawOpen) {
        clearInterval(poll);
        // Twice: once now, once after the mount finishes collapsing, because the
        // teardown reflow can move the page again a frame or two later.
        window.scrollTo(0, y);
        setTimeout(function () { window.scrollTo(0, y); }, 60);
        return;
      }

      // Safety valve so a customer who never closes the builder leaves no timer behind.
      if (++ticks > 3600) clearInterval(poll);
    }, 250);
  }

  function open(el) {
    busy(el, true);
    setStatus(el, '');
    guardScroll();

    fetchConfig(el)
      .then(function (cfg) {
        var init = buildInit(el, cfg);

        if (!init.bagVariantId) {
          // Opening cold beats silently pre-selecting the wrong bag.
          track('cali_strapcta_error', {
            reason: 'no_builder_variant_for_sku',
            bagSku: init.bagSku,
            pdpVariantId: init.pdpVariantId
          });
        }

        track('cali_strapcta_click', {
          bagSku: init.bagSku,
          startingPoint: init.startingPoint,
          builderHandle: el.getAttribute('data-builder-handle')
        });

        window.caliBuilderInit = init;

        if (LOADED) {
          if (typeof window.caliBuilderOpen === 'function') {
            window.caliBuilderOpen(init);
            busy(el, false);
            focusStep(init.startStep);
            track('cali_strapcta_open', { reopen: true, bagSku: init.bagSku });
            return null;
          }
          throw new Error('builder loaded but caliBuilderOpen missing (unpatched bundle?)');
        }

        applyGlobals(cfg);
        ensureMount(cfg);
        return injectBundle(el).then(function () {
          // The bundle parsing is not the same as the patches running: an
          // unpatched bundle loads fine but never defines caliBuilderOpen and
          // never auto-opens, which reads as a dead button. Poll briefly and
          // surface a real error instead of a silent first click.
          return new Promise(function (resolve, reject) {
            var tries = 0;
            (function check() {
              if (typeof window.caliBuilderOpen === 'function') {
                busy(el, false);
                focusStep(init.startStep);
                track('cali_strapcta_open', { reopen: false, bagSku: init.bagSku });
                return resolve(null);
              }
              if (++tries > 25) {
                return reject(new Error('bundle loaded without strap patches (caliBuilderOpen never appeared)'));
              }
              setTimeout(check, 200);
            })();
          });
        });
      })
      .catch(function (err) {
        busy(el, false);
        setStatus(el, "Couldn't open the builder. Please try again.", 'error');
        track('cali_strapcta_error', { reason: String((err && err.message) || err) });
      });
  }

  function init() {
    var el = root();
    if (!el) return;
    var btn = el.querySelector('[data-cali-strap-cta-btn]');
    if (!btn) return;
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      open(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
